/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/logger/LoggingAgent.ts
 * @create:      2025-06-20 16:06:46.155
 * @modify:      2025-10-14 22:39:26.169
 * @version:     6.1.0
 * @times:       46
 * @lines:       154
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { LogLevel } from "../enum/LogLevel";
import { LogType } from "../enum/LogType";
import { ILogColors } from "../interface/ILogColors";
import { FormatLog } from "../Log";
import { ILogger } from "../interface/ILogger";
import { ILogOptions } from "../interface/ILogOptions";
import { ILoggingAgent } from "../interface/ILoggingAgent";

/**
 * @since 5.0.0
 * @version 2025-10-14
 */
export class LoggingAgent implements ILoggingAgent {
    public readonly Groups = new Set<string>();
    public readonly Handlers = new Set<ILogger>();

    public InfoColors?: ILogColors;
    public WarnColors?: ILogColors;
    public ErrorColors?: ILogColors;

    public constructor(...loggers: ILogger[]) {
        for (const logger of loggers) {
            this.Handlers.add(logger);
        }
    }

    public Info(data: unknown, options?: ILogOptions): void {
        if (!this.checkGroup(options?.Group)) {
            return;
        }

        const log = this.buildLog(LogLevel.Info, data, options);
        if (log === undefined) {
            return;
        }

        this.write(log, LogLevel.Info, options?.Type, options?.Group);
    }

    public Warn(data: unknown, options?: ILogOptions): void {
        if (!this.checkGroup(options?.Group)) {
            return;
        }

        const log = this.buildLog(LogLevel.Warn, data, options);
        if (log === undefined) {
            return;
        }

        this.write(log, LogLevel.Warn, options?.Type, options?.Group);
    }

    public Error(data: unknown, options?: ILogOptions): void {
        if (!this.checkGroup(options?.Group)) {
            return;
        }

        const log = this.buildLog(LogLevel.Error, data, options);
        if (log === undefined) {
            return;
        }

        this.write(log, LogLevel.Error, options?.Type, options?.Group);
    }

    protected checkGroup(group?: string) {
        if (group === undefined) {
            return true;
        }

        if (this.Groups.has(group)) {
            return true;
        }

        return false;
    }

    protected getColors(level: LogLevel) {
        if (level === LogLevel.Info) {
            return this.InfoColors;
        }

        if (level === LogLevel.Warn) {
            return this.WarnColors;
        }

        if (level === LogLevel.Error) {
            return this.ErrorColors;
        }

        return undefined;
    }

    protected buildLog(level: LogLevel, data: unknown, options?: ILogOptions): string | undefined {
        const formatOptions = options ?? {};
        if (formatOptions.Colors === undefined) {
            formatOptions.Colors = this.getColors(level);
        }

        const log = FormatLog(`${data}`, level, formatOptions);
        return log;
    }

    protected write(data: string, level: LogLevel, type?: LogType, group?: string) {
        this.Handlers.forEach(handler => {
            if (type) {
                if ((type & handler.Type) === 0) {
                    return;
                }
            }

            if (group) {
                if (handler.Groups?.includes(group) === false) {
                    return;
                }
            }

            handler.Write(data, level);
        });
    }
}
